/**
 * Photo downscaling for recognition + the audit-trail record.
 *
 * One downscale step feeds both purposes — no separate full-res original is
 * ever kept, so IndexedDB doesn't balloon across many games.
 */

export const MAX_ORIGINAL_FILE_BYTES = 20_000_000; // pre-downscale ceiling, native camera photos included

export interface FileSizeCheck {
  ok: boolean;
  error?: string;
}

/** Cheap sync guard — call before starting any FileReader/canvas work. */
export function checkOriginalFileSize(file: File): FileSizeCheck {
  if (file.size > MAX_ORIGINAL_FILE_BYTES) {
    const mb = (file.size / 1_000_000).toFixed(1);
    const maxMb = MAX_ORIGINAL_FILE_BYTES / 1_000_000;
    return { ok: false, error: `That photo is ${mb}MB — please use one under ${maxMb}MB.` };
  }
  return { ok: true };
}

/** Pure arithmetic — scales so the longer edge is at most maxEdge, preserving aspect ratio. */
export function computeScaledDimensions(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdge || longEdge === 0) {
    return { width, height };
  }
  const scale = maxEdge / longEdge;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export interface DownscaleOptions {
  maxEdge?: number;
  quality?: number;
  mimeType?: string;
}

/** Downscales an image file to a JPEG data URL. Browser-only — not unit-testable without a real canvas. */
export async function downscaleImageFile(file: File, opts: DownscaleOptions = {}): Promise<string> {
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = computeScaledDimensions(bitmap.width, bitmap.height, opts.maxEdge ?? 1600);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable.');
    ctx.drawImage(bitmap, 0, 0, width, height);
    return canvas.toDataURL(opts.mimeType ?? 'image/jpeg', opts.quality ?? 0.85);
  } finally {
    bitmap.close();
  }
}
